/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
 
import React, { useEffect, useState } from 'react';
import styles from './EmbryoCulture.module.css';
import EmbryoCultureImg  from '../../../assets/SVG/EmbryoCulture.png';
import Grid from '@mui/material/Grid';
import {  Embryo, getEmbryoCulturel, getPGTResult, PGTEmbryoData } from './EmbryoCultureHelper';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useLoader } from '../../loaderProvider/loader';
import { getSemenProcessingPreProcesss, SumationData } from '../../../pages/Embryology/Embryo_Culture/AllDayHelper';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import { useTranslation } from 'react-i18next';

interface EmbryoCultureProps {
  cycle: string;
}

const EmbryoCulture: React.FC<EmbryoCultureProps> = ({ cycle }) => {
  const { showLoader, hideLoader } = useLoader();
  const { t } = useTranslation('cycleOverview_config');
  const [dayData, setDayData] = useState<any[]>([]);
  const [summationData, setSummationData] = useState<SumationData>();

  
  useEffect(() => {
    const fetchData = () => {
      showLoader();
      getEmbryoCulturel(cycle)
        .then((response) => {

          const relevantGrades = ['GV', 'M I', 'M II'];
          const relevantActions = ['freeze', 'culture', 'discard', 'transfer'];

          // Maps to group data by day
          const day0Map = new Map<string, number>();
          const day1Map = new Map<string, number>();
          const day2Map = new Map<string, number>();
          const day3Map = new Map<string, number>();
          const day4Map = new Map<string, number>();
          const day5Map = new Map<string, number>();
          const day6Map = new Map<string, number>();
          const day7Map = new Map<string, number>();

          // Iterate through embryos
          response.embryo.forEach((embryo: any) => {
            const { day, oocyte_grade, grade, action, pn, pb, number_of_cells, stage, fragmentation_percentage, insemination_technique } = embryo;
            const actionKey = action?.toLowerCase();

            // Day 0: group by oocyte grade + action + insemination_technique
            if (day === 0 && relevantGrades.includes(oocyte_grade) && relevantActions.includes(actionKey)) {
              const key = `${oocyte_grade}_${actionKey}_${insemination_technique || '-'}`;
              day0Map.set(key, (day0Map.get(key) || 0) + 1);
            }

            // Day 1: group by action + PN
            if (day === 1 && relevantActions.includes(actionKey)) {
              const pnKey = pn != null ? String(pn) : '0';
              const pbKey = pb != null ? String(pb) : '0';
              const key = `${actionKey}_${pnKey}_${pbKey}`;
              day1Map.set(key, (day1Map.get(key) || 0) + 1);
            }

            // Day 2: group by grade + action + fragmentation_percentage + number_of_cells
            if (day === 2 && grade && fragmentation_percentage && relevantActions.includes(actionKey)) {
              const key = `${grade}|||${actionKey}|||${fragmentation_percentage}|||${number_of_cells ?? '0'}`;
              day2Map.set(key, (day2Map.get(key) || 0) + 1);
            }

            // Day 3: group by grade + action + number_of_cells
            if (day === 3 && grade && relevantActions.includes(actionKey)) {
              const key = `${grade}_${actionKey}_${number_of_cells ?? '0'}`;
              day3Map.set(key, (day3Map.get(key) || 0) + 1);
            }

            // Day 4–7: group by stage + grade
            if (day === 4 && stage && grade && actionKey) {
              const key = `${stage}|||${grade}|||${actionKey}`;
              day4Map.set(key, (day4Map.get(key) || 0) + 1);
            }
            if (day === 5 && stage && grade && actionKey) {
              const key = `${stage}|||${grade}|||${actionKey}`;
              day5Map.set(key, (day5Map.get(key) || 0) + 1);
            }
            if (day === 6 && stage && grade && actionKey) {
              const key = `${stage}|||${grade}|||${actionKey}`;
              day6Map.set(key, (day6Map.get(key) || 0) + 1);
            }
            if (day === 7 && stage && grade && actionKey) {
              const key = `${stage}|||${grade}|||${actionKey}`;
              day7Map.set(key, (day7Map.get(key) || 0) + 1);
            }
          });

          // Formatters
          const formattedDay0 = Array.from(day0Map.entries()).map(([key, count]) => {
            const [oocyte_grade, action, insemination_technique] = key.split('_');
            return { day: 0, oocyte_grade, action, count, insemination_technique };
          });

          const formattedDay1 = Array.from(day1Map.entries()).map(([key, count]) => {
            const [action, pn, pb] = key.split('_');
            return {
              day: 1,
              action,
              pn,
              pb,
              count,
            };
          });

          const formattedDay2 = Array.from(day2Map.entries()).map(([key, count]) => {
            const [grade, action, fragmentation_percentage, number_of_cells] = key.split('|||');
            return { day: 2, grade, action, count, fragmentation_percentage, number_of_cells };
          });

          const formattedDay3 = Array.from(day3Map.entries()).map(([key, count]) => {
            const [grade, action, number_of_cells] = key.split('_');
            return {
              day: 3,
              grade,
              action,
              number_of_cells: number_of_cells === 'unknown' ? '0' : number_of_cells,
              count,
            };
          });

          const formatDayMap = (map: Map<string, number>, day: number) =>
            Array.from(map.entries()).map(([key, count]) => {
              const [stage, grade, action] = key.split('|||');
              return { day, stage, grade, action, count };
            });

          const formattedDay4 = formatDayMap(day4Map, 4);
          const formattedDay5 = formatDayMap(day5Map, 5);
          const formattedDay6 = formatDayMap(day6Map, 6);
          const formattedDay7 = formatDayMap(day7Map, 7);

          const finalGroupedData = [
            { day: 0, items: formattedDay0 },
            { day: 1, items: formattedDay1 },
            { day: 2, items: formattedDay2 },
            { day: 3, items: formattedDay3 },
            { day: 4, items: formattedDay4 },
            { day: 5, items: formattedDay5 },
            { day: 6, items: formattedDay6 },
            { day: 7, items: formattedDay7 },
          ].filter(group => group.items.length > 0);

          const embryoData: Embryo[] = response.embryo;
          embryoData.sort((a, b) => a.day - b.day);
          setDayData(finalGroupedData);
        })
        .catch((err) => {
          console.error('Error fetching embryo data:', err);
        })
        .finally(() => {
          hideLoader();
        });

      getSemenProcessingPreProcesss(Number(cycle))
        .then((response) => {
          setSummationData(response.summation);
        }).catch((err) => {
          console.error('Error fetching embryo data:', err);
        })
        .finally(() => {
          hideLoader();
        });
        
    };
  
    fetchData();
  }, [cycle, showLoader, hideLoader]);
  
  const [data, setData] = useState<PGTEmbryoData[]>([]); 

  useEffect(() => {
    getPGTResult(cycle)
      .then((response: PGTEmbryoData[]) => {
        console.log('PGT Result Data:', response);
        setData(response);
      })
      .catch((error) => {
        console.error('Error fetching PGT result:', error);
      });
  }, [cycle]); 
  
  return (
    <div className={styles.container}>
      <Grid container spacing={3}>
        <Grid xs={12} sm={12} md={12} lg={12} xl={6}>
          <Grid container direction="row" justifyContent="center" alignItems="center" spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <img src={EmbryoCultureImg} alt="Denudation Dish"  
                style={{
                  width: '60%',
                  height: '10%',
                  objectFit: 'cover',
                  display: 'block', 
                  margin: '0 auto'
                }} 
              />
            </Grid>
          </Grid>

          <div className='mt-4'>
    
      
            <TableContainer sx={{ width: '80%', margin: '0 auto' }}> 
              <span className={`${styles.daytext} mt-4`}>{t('PGTDetails')}</span>
              <Table className='mt-4'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}><strong>{t('EmbryoNo')}</strong></TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}><strong>{t('Sex')}</strong></TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}><strong>{t('Karyotype')}</strong></TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}><strong>{t('Result')}</strong></TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}><strong>{t('Mitosure')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data && data.length >0 ? (
                    data.map((item,) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{item.embryo_number}</TableCell>
                        <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{convertUnderscoreToTitle(item.pgt_result_embryo_sex)}</TableCell>
                        <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{convertUnderscoreToTitle(item.action)}</TableCell>
                        <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{convertUnderscoreToTitle(item.pgt_results)}</TableCell>
                        <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{convertUnderscoreToTitle(item.pgt_result_moisture)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', border: '1px solid rgba(224, 224, 224, 1)' }}>{t('NoDataAvailable')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </div>
        </Grid>


        <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className={styles.subcontainer}>
            <Grid container spacing={2}>

              <Grid item xs={12} sm={12} md={3} lg={3} xl={3} className='mt-4'>
                <div className={styles.containerBOX}>
                  <div className={styles.box}></div>
                  <span className={styles.headerTextStyle}>
                    {t('Freezed')} <span className={styles.customTextStyle}>{summationData &&summationData.freezed ?summationData.freezed:0 }</span>
                  </span>
                </div>
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3} xl={3} className='mt-4'>
                <div className={styles.containerBOX}>
                  <div className={styles.boxEmbryos}></div>
                  <span className={styles.headerTextStyle}>
                    {t('Transferred')} <span className={styles.customTextStyle}>{summationData &&summationData.transferred ?summationData.transferred:0 }</span>
                  </span>
                </div>
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3} xl={3} className='mt-4'>
                <div className={styles.containerBOX}>
                  <div className={styles.boxCulture}></div>
                  <span className={styles.headerTextStyle}>
                    {t('Culture')} <span className={styles.customTextStyle}>{summationData &&summationData.culture ?summationData.culture:0 }</span>
                  </span>
                </div>
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3} xl={3} className='mt-4'>
                <div className={styles.containerBOX}>
                  <div className={styles.boxDiscard}></div>
                  <span className={styles.headerTextStyle}>
                    {t('Discard')} <span className={styles.customTextStyle}>{summationData &&summationData.discarded ?summationData.discarded:0 }</span>
                  </span>
                </div>
              </Grid>

            </Grid>
          </Grid>
          {dayData.map((day, index) => (
            day.items.length > 0 && (
              <div key={index}>
                <hr className="mt-4" />
                <span className={styles.daytext}>{`${t('Day')} ${day.day}`}</span>
                <Grid item xs={12} className="mt-4">
                  <Grid container direction="row" spacing={2}>
                    {day.items.map((item: any, itemIndex: number) => {
                      let bgColor = '#FFF'; 
                      const action = item.action?.toLowerCase?.();

                      if (action === 'freeze') bgColor = '#f8cccb';
                      else if (action === 'transfer') bgColor = '#D36B69';
                      else if (action === 'discard') bgColor = '#b9b9b9';

                      return (
                        <Grid item xs={12} sm={3} md={6} lg={4} key={itemIndex} className="mt-4">
                          <div className={styles.containerd}>
                            <div className={styles.circle} style={{ backgroundColor: bgColor }}>
                              {item.count}
                            </div>
                            <div className={styles.text}>
                              {day.day === 0 && (
                                <>
                                  {item.oocyte_grade} -
                                  {item.insemination_technique && (
                                    <span className={styles.inseminationTechnique}>
                                      {' '}{item.insemination_technique}
                                    </span>
                                  )}
                                </>
                              )}
                              {day.day === 1 && ` PN ${item.pn} - ${item.pb}`}
                              {day.day === 2 && `Grade ${item.grade} - ${convertUnderscoreToTitle(item.fragmentation_percentage ?? 0)}% - ${item.number_of_cells || '0'} Cells`}
                              {day.day === 3 && `${item.number_of_cells || '0'} Cells - Grade ${item.grade}`}
                              {(day.day >= 4 && day.day <= 7) && `${convertUnderscoreToTitle(item.stage)} - ${item.grade}`}
                            </div>
                          </div>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </div>
            )
          ))}




        </Grid>
      </Grid>
    </div>
  );
};

export default EmbryoCulture;
